import Highcharts from "highcharts/esm/highcharts.src"

import "highcharts/esm/modules/drilldown.src"
import "highcharts/esm/modules/no-data-to-display.src"

import { useRef, useState } from "react"
import ReactServer from "react-dom/server"
import styled from "styled-components"

import { Banner, colors, Text } from "@minuthq/meatball-ui-react"
import { components } from "@minuthq/minut-api-types/schema"

import { GraphCard } from "src/components/Homes/DeviceDetails/Overview/GraphCard"
import { useOrganization } from "src/data/organizations/hooks/useOrganization"
import {
  useDeviceStatusDrilldownByHomegroupId,
  useFetchDeviceStatusByOrganization,
} from "src/data/portfolioAnalytics/portfolioAnalyticsQueries"
import { useGetUser } from "src/data/user/hooks/useGetUser"
import { useTranslate } from "src/i18n/useTranslate"
import { BarChart } from "src/ui/Graphs/BarChart"
import { TBarChartMultiSeriesData } from "src/ui/Graphs/graphTypes"
import { MCard } from "src/ui/MCard/MCard"
import { spacing } from "src/ui/spacing"
import { ErrorService } from "src/utils/ErrorService"
import { getStoredSelectedTimezone } from "src/utils/l10n"

const GRAPH_HEIGHT = 300

export function PortfolioAnalyticsDeviceStatusGraph() {
  const { t, langKeys } = useTranslate()
  const user = useGetUser()
  const timezone = getStoredSelectedTimezone()
  const [error, setError] = useState<string | null>(null)

  // The drilldown callback fires once for each series in the chart, which all fire one mutation
  // each time, so we need to prevent multiple drilldowns from being triggered.
  // We can't use the isLoading on the mutation since the callback has time to fire multiple times before starting the mutation
  const isLoadingDrilldown = useRef<boolean>(false)

  const { orgId } = useOrganization()

  const fetchDeviceStatusByOrganization = useFetchDeviceStatusByOrganization({
    orgId,
  })

  const deviceStatusDrilldownByHomegroupId =
    useDeviceStatusDrilldownByHomegroupId({
      orgId,
    })

  const deviceStatusByOrganization = fetchDeviceStatusByOrganization.data
  const { isLoading } = fetchDeviceStatusByOrganization

  function hasNonZeroTotal(
    item: components["schemas"]["DeviceStatus"]["data"][0]
  ) {
    const total =
      item.aggregation.all_online +
      item.aggregation.some_offline +
      item.aggregation.all_offline +
      item.aggregation.no_devices
    return total > 0
  }

  function formatDeviceStatusDrilldownData(
    analytics: components["schemas"]["DeviceStatus"][]
  ) {
    if (!analytics || !analytics[0]?.data) {
      return []
    }

    const data = analytics[0].data.filter(hasNonZeroTotal)

    const series: Highcharts.Options["series"] = [
      {
        name: t(langKeys.all_online),
        data: data.map((item) => [
          item.metadata.name,
          item.aggregation.all_online,
        ]),
        color: colors.systemGoodLight,
        borderColor: colors.systemGoodDark,
        type: "column",
      },
      {
        name: t(langKeys.some_offline),
        data: data.map((item) => [
          item.metadata.name,
          item.aggregation.some_offline,
        ]),
        color: colors.systemWarningLight,
        borderColor: colors.systemWarningDark,
        type: "column",
      },
      {
        name: t(langKeys.all_offline),
        data: data.map((item) => [
          item.metadata.name,
          item.aggregation.all_offline,
        ]),
        color: colors.systemErrorLight,
        borderColor: colors.systemErrorDark,
        type: "column",
      },
      {
        name: t(langKeys.no_devices),
        data: data.map((item) => [
          item.metadata.name,
          item.aggregation.no_devices,
        ]),
        color: colors.neutralDark3,
        borderColor: colors.neutralDark3,
        type: "column",
      },
    ]

    return series
  }

  function onDrilldown(e: Highcharts.DrilldownEventObject) {
    if (!e.seriesOptions && !isLoadingDrilldown.current && e.point.drilldown) {
      const chart = e.target

      chart?.showLoading(t(langKeys.loading))
      isLoadingDrilldown.current = true
      setError(null)

      deviceStatusDrilldownByHomegroupId.mutate(e.point.drilldown, {
        onSuccess: (data) => {
          const drilldowns = formatDeviceStatusDrilldownData(data.analytics)

          // For some reason Highcharts won't render the drilldown series unless there's at least a ms timeout here
          setTimeout(() => {
            chart?.hideLoading()

            drilldowns.forEach((drilldownData) => {
              chart?.addSingleSeriesAsDrilldown?.(e.point, drilldownData)
            })

            chart?.applyDrilldown?.()
            isLoadingDrilldown.current = false
          }, 1)
        },
        onError: (error) => {
          ErrorService.captureException(error)
          chart?.hideLoading()
          isLoadingDrilldown.current = false
          setError(t(langKeys.failed_something_went_wrong))
        },
      })
    }
  }

  const deviceStatusData: TBarChartMultiSeriesData[] = [
    {
      name: t(langKeys.all_online),
      data:
        deviceStatusByOrganization?.analytics?.flatMap((analytics) =>
          analytics.data.filter(hasNonZeroTotal).map((item) => ({
            name: item.metadata.name,
            y: item.aggregation.all_online,
            drilldown:
              item.grouped_by === "no_homegroup" ? undefined : item.grouped_by,
          }))
        ) ?? [],
      fillColor: colors.systemGoodLight,
      borderColor: colors.systemGoodDark,
    },
    {
      name: t(langKeys.some_offline),
      data:
        deviceStatusByOrganization?.analytics?.flatMap((analytics) =>
          analytics.data.filter(hasNonZeroTotal).map((item) => ({
            name: item.metadata.name,
            y: item.aggregation.some_offline,
            drilldown:
              item.grouped_by === "no_homegroup" ? undefined : item.grouped_by,
          }))
        ) ?? [],
      fillColor: colors.systemWarningLight,
      borderColor: colors.systemWarningDark,
    },
    {
      name: t(langKeys.all_offline),
      data:
        deviceStatusByOrganization?.analytics?.flatMap((analytics) =>
          analytics.data.filter(hasNonZeroTotal).map((item) => ({
            name: item.metadata.name,
            y: item.aggregation.all_offline,
            drilldown:
              item.grouped_by === "no_homegroup" ? undefined : item.grouped_by,
          }))
        ) ?? [],
      fillColor: colors.systemErrorLight,
      borderColor: colors.systemErrorDark,
    },
    {
      name: t(langKeys.no_devices),
      data:
        deviceStatusByOrganization?.analytics?.flatMap((analytics) =>
          analytics.data.filter(hasNonZeroTotal).map((item) => ({
            name: item.metadata.name,
            y: item.aggregation.no_devices,
            drilldown:
              item.grouped_by === "no_homegroup" ? undefined : item.grouped_by,
          }))
        ) ?? [],
      fillColor: colors.neutralDark3,
    },
  ]

  return (
    <GraphCard
      title={t(langKeys.portfolio_analytics_device_status_title)}
      titleVariant="heading3"
      info={t(langKeys.portfolio_analytics_device_status_info)}
      isLoading={isLoading}
    >
      {error && <ErrorBanner type="error">{error}</ErrorBanner>}
      <BarChart
        dataSeriesType="multi"
        data={deviceStatusData}
        timezone={timezone.timezone.tzCode}
        stackSeries
        clockType={user.clock_type}
        showLegend
        height={GRAPH_HEIGHT}
        borderRadius={2}
        seriesHoverBrightness={0.03}
        interactive
        yAxisOptions={{
          title: {
            text: t(langKeys.number_of_units),
          },
        }}
        xAxisOptions={{
          type: "category",
        }}
        onDrilldown={onDrilldown}
        drilldownOptions={{
          allowPointDrilldown: false,
          series: [],
          breadcrumbs: {
            formatter: function (this, context) {
              if (context.level === 0) {
                return ReactServer.renderToString(
                  <Text variant="bodyS">{t(langKeys.back_to_overview)}</Text>
                )
              }

              return ReactServer.renderToString(
                <Text variant="bodyS">{context.levelOptions.name}</Text>
              )
            },
          },
          activeAxisLabelStyle: {
            color: colors.textPrimary,
          },
        }}
        tooltip={{
          disabled: false,
          decimals: 0,
          stickOnContact: false,
          unit: "",
          formatter: ({ points }) => {
            const total =
              points?.reduce((sum, point) => sum + (point.y ?? 0), 0) ?? 0

            return (
              <MCard padding={0}>
                <TooltipTitle>
                  <Text variant="bodyS">{points?.[0]?.name}</Text>
                </TooltipTitle>
                <PointsContainer>
                  {points?.map((point) => {
                    if (!point.y) {
                      return null
                    }

                    return (
                      <div key={point.series.name}>
                        <Text variant="subtitleS">
                          {t(langKeys.portfolio_analytics_graph_tooltip, {
                            count: point.y,
                            name: point.series.name,
                          })}
                        </Text>
                        <Text variant="bodyS">
                          {total > 0
                            ? `(${(((point.y ?? 0) / total) * 100).toFixed(0)}%)`
                            : "(0%)"}
                        </Text>
                      </div>
                    )
                  })}
                </PointsContainer>
              </MCard>
            )
          },
        }}
      />
    </GraphCard>
  )
}

const TooltipTitle = styled.div`
  background-color: ${colors.primaryLight};
  padding: ${spacing.XS} ${spacing.M};
`

const PointsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.M};
  padding: ${spacing.XS} ${spacing.M};
`

const ErrorBanner = styled(Banner)`
  margin-bottom: ${spacing.M};
`
